import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import * as SharedGridActions from './shared-grid.actions';
import * as StockManagerActions from '../../../../app/features/stock-manager/store/stock-manager.actions';
import {EMPTY, forkJoin, from, of} from 'rxjs';
import {catchError, map, mergeMap, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {StockItem, VatRates} from '../../../shared-utilities/models-old/datastructures';
import {IPriceBandPerStore} from '../../../shared/shared-models/price-banding/price-band';
import * as NGPReportActions from '../../../features/ngp-report/store/ngp-report.actions';
import * as AutoOrderActions from '../../../features/auto-ordering/store/auto-ordering.actions';
import {NGPReport} from '../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {Action, select, Store} from '@ngrx/store';
import {selectAllVatRates} from '../../../features/ngp-report/store/ngp-report.selectors';
import {IStore} from '../../../shared/shared-models/store/store';
import {path_users_userId_settings_table_navigation} from '../../../shared/shared-services/database-paths';
import {
  CollectionUsersSettingsService,
} from '../../../shared/shared-services/firebase/collection-users-settings.service';
import * as UserAccessSelectors from '../../../features/user-access/user-store/user.selectors';
import * as UserStoreSelectors from '../../../features/user-access/user-stores-store/user-store.selectors';
import {
  selectCurrentNavigationObj,
  selectStores,
} from '../../../features/user-access/user-stores-store/user-store.selectors';
import {ITableNavSettings} from '../../../features/settings-new/models';

@Injectable()
export class SharedGridEffects {

//===============================================================================================================
// Get Table Nav Settings
//===============================================================================================================

  // '[Shared Grid][Table Nav] Get Table Nav Settings'
  getTableNavSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedGridActions.getTableNavSettings),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(this.collectionUsersSettingsService.getDocument<ITableNavSettings>(
          path_users_userId_settings_table_navigation(user.id),
        )).pipe(
          map((data: ITableNavSettings) => {
            if (!data.cellDebounce) {
              data.cellDebounce = 1500;
            }
            return SharedGridActions.getTableNavSettingsSuccess({settings: data});
          }),
          catchError((error) =>
            of(
              SharedGridActions.getTableNavSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // '[Shared Grid][Price Banding] Get Price Banding for Stores'
  getPriceBandingForStores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedGridActions.getPriceBandingForStores),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) =>
        from(this.firebaseService.getPriceBandingForStores()).pipe(
          map((priceBandsPerStore: IPriceBandPerStore) => {
            return SharedGridActions.getPriceBandingForStoresSuccess({priceBandsPerStore});
          }),
          catchError((error) => of(SharedGridActions.getPriceBandingForStoresFailure({
            error: {
              ...error,
              action: JSON.stringify(action),
              actionType: action.type,
              originalError: error,
              storeIds: [store.storeId],
              userId: user.id,
            },
          }))),
        ),
      ),
    ),
  );

//===============================================================================================================
// Price Banding
//===============================================================================================================
  //===============================================================================================================
  setItemDisabledStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedGridActions.setItemDisabledStatus),
      withLatestFrom(this.store.pipe(select(selectCurrentNavigationObj))),
      withLatestFrom(this.store.pipe(select(UserStoreSelectors.selectSelectedStore))),
      mergeMap(([[{item, enable}, currentPage], selectedStore]) => {
        switch (currentPage.currentSelectedPage.currentPage) {
          case 'ngp-report':
            return of(NGPReportActions.setItemDisabledStatus({ngpReport: item as NGPReport, enable}));
          case 'stock-manager':
            return of(StockManagerActions.setItemDisabledStatus({stockItem: item as StockItem, enable}));
          case 'auto-ordering':
            return of(AutoOrderActions.setStockItemDisabledFromOrder({
              enable,
              item: item as StockItem,
              storeId: selectedStore?.storeId,
            }));
          default:
            return EMPTY;
        }
      }),
    ),
  );

  //===============================================================================================================
  // set Item Disabled
  getVatRateConversion$ = createEffect(() => this.actions$.pipe(
    ofType(SharedGridActions.getVatRateConversion),
    withLatestFrom(
      this.store.pipe(select(selectStores)),
      this.store.pipe(select(selectAllVatRates)),
    ),
    switchMap(([action, userStores, vatRates]) => {
      const actions: Action[] = [];
      if (Object.keys(vatRates).length > 0) {
        actions.push(SharedGridActions.mergeVatRatesAndFetchMissing({
          userStores,
          vatRates: vatRates,
        }));
      } else {
        actions.push(SharedGridActions.getVatRateConversionWithoutExistingVatRates({
          userStores,
        }));
      }
      return actions;
    }),
  ));
  //===============================================================================================================
  // Merge cached VAT rates; fetch any stores missing from the ngp-report cache via Firestore.
  //===============================================================================================================
  mergeVatRatesAndFetchMissing$ = createEffect(() => this.actions$.pipe(
    ofType(SharedGridActions.mergeVatRatesAndFetchMissing),
    switchMap(({userStores, vatRates}) => {
      const storesMissingVatRates = userStores.filter((userStore: IStore) => !vatRates[userStore.storeId]);

      if (storesMissingVatRates.length === 0) {
        const vatRatesResult: Record<string, VatRates> = userStores.reduce(
          (acc: Record<string, VatRates>, userStore: IStore) => {
            acc[userStore.storeId] = vatRates[userStore.storeId];
            return acc;
          },
          {},
        );
        return of(SharedGridActions.getVatRateConversionSuccess({vatRates: vatRatesResult}));
      }

      return forkJoin(
        storesMissingVatRates.map((userStore: IStore) =>
          this.firebaseService.subStoreDoc('data/singular_documents/vat_rates', userStore.storeId).pipe(
            take(1),
            map((vr: VatRates) => ({storeId: userStore.storeId, vatRate: vr ?? {}})),
          ),
        ),
      ).pipe(
        map((fetchedRates: { storeId: string; vatRate: VatRates }[]) => {
          const fetchedByStoreId: Record<string, VatRates> = fetchedRates.reduce(
            (acc: Record<string, VatRates>, entry: { storeId: string; vatRate: VatRates }) => {
              acc[entry.storeId] = entry.vatRate;
              return acc;
            },
            {},
          );
          const vatRatesResult: Record<string, VatRates> = userStores.reduce(
            (acc: Record<string, VatRates>, userStore: IStore) => {
              acc[userStore.storeId] = vatRates[userStore.storeId] ?? fetchedByStoreId[userStore.storeId] ?? {};
              return acc;
            },
            {},
          );
          return SharedGridActions.getVatRateConversionSuccess({vatRates: vatRatesResult});
        }),
        catchError((error) => of(SharedGridActions.getVatRateConversionFailure({
          error: {
            ...error,
            message: error?.message ?? 'Failed to load VAT rates for one or more stores',
            name: 'GetVatRateConversionError',
          },
        }))),
      );
    }),
  ));

  getVatRateConversionWithoutExistingVatRates$ = createEffect(() => this.actions$.pipe(
    ofType(SharedGridActions.getVatRateConversionWithoutExistingVatRates),
    withLatestFrom(
      this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
      this.store.pipe(select(UserAccessSelectors.selectUser)),
    ),
    switchMap(([action, store, user]) => {
      const {userStores} = action;
      const vatRatesObservables = userStores.map((userStore: IStore) =>
        this.firebaseService.subStoreDoc('data/singular_documents/vat_rates', userStore.storeId)
          .pipe(
            take(1),
            map((vr: VatRates) => ({storeId: userStore.storeId, vatRate: vr})),
          ),
      );
      return forkJoin(vatRatesObservables).pipe(
        map((vatRatesArray) => {
          const vatRates = vatRatesArray.reduce((acc, {storeId, vatRate}) => {
            acc[storeId] = vatRate;
            return acc;
          }, {});
          return SharedGridActions.getVatRateConversionSuccess({vatRates});
        }),
        catchError((error) => of(SharedGridActions.getVatRateConversionFailure({
          error: {
            ...error,
            action: JSON.stringify(action),
            actionType: action.type,
            originalError: error,
            storeIds: [store.storeId],
            userId: user.id,
          },
        }))),
      );
    }),
  ));

  hasUpdatedFirebase = false;

  // '[Shared Grid][Table Nav] Get Table Nav Settings Success',
  setTableNavSettingsInFirebase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SharedGridActions.getTableNavSettingsSuccess),
      withLatestFrom(
        this.store.pipe(select(UserStoreSelectors.selectSelectedStore)),
        this.store.pipe(select(UserAccessSelectors.selectUser)),
      ),
      mergeMap(([action, store, user]) => {
        const {settings} = action;
        if (this.hasUpdatedFirebase) {
          return from(this.collectionUsersSettingsService.setDocument<ITableNavSettings>(
            path_users_userId_settings_table_navigation(user.id), settings),
          ).pipe(
            map(() => {
              return {type: '[Shared Grid][Table Nav] Table Nav Settings Set on Firebase'};
            }),
            catchError((error) => {
              return of(SharedGridActions.getTableNavSettingsFailure({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                  storeIds: [store.storeId],
                  userId: user.id,
                },
              }));
            }),
          );
        } else {
          this.hasUpdatedFirebase = true;
          return of({type: 'Settings Fetched From Firebase '});
        }
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private readonly store: Store,
    private firebaseService: FirebaseService,
    private collectionUsersSettingsService: CollectionUsersSettingsService,
  ) {
  }
}
